<template>
  <div class="offline-detector">
    <!-- 离线状态遮罩 -->
    <Teleport to="body">
      <Transition name="fade">
        <div
          v-if="showOfflineOverlay && !isOnline"
          class="fixed inset-0 bg-gray-900 bg-opacity-75 flex items-center justify-center z-50"
        >
          <div class="bg-white rounded-lg p-6 max-w-md mx-4 text-center">
            <div class="mb-4">
              <n-icon :component="WifiOff" size="48" class="text-red-500 mx-auto" />
            </div>
            
            <h3 class="text-lg font-semibold text-gray-900 mb-2">
              The network connection has been disconnected
            </h3>
            
            <p class="text-gray-600 mb-4">
              Please check your internet connection and try again.
            </p>
            
            <div class="space-y-3">
              <n-button
                type="primary"
                block
                @click="checkConnection"
                :loading="checking"
              >
                Reconnect
              </n-button>
              
              <n-button
                quaternary
                block
                @click="continueOffline"
              >
                Continue offline
              </n-button>
            </div>
            
            <!-- 连接提示 -->
            <div class="mt-4 text-sm text-gray-500">
              <p>Some features may not be available in offline mode</p>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>

    <!-- 离线状态栏 -->
    <Teleport to="body">
      <Transition name="slide-down">
        <div
          v-if="showOfflineBar && !isOnline && !showOfflineOverlay"
          class="fixed top-0 left-0 right-0 bg-red-500 text-white z-40"
        >
          <div class="flex items-center justify-between px-4 py-2">
            <div class="flex items-center space-x-2">
              <n-icon :component="WifiOff" />
              <span class="text-sm">Offline Mode</span>
            </div>
            
            <div class="flex items-center space-x-2">
              <n-button
                size="small"
                quaternary
                @click="checkConnection"
                :loading="checking"
                class="text-white hover:bg-red-600"
              >
                Retry
              </n-button>
              
              <n-button
                size="small"
                quaternary
                circle
                @click="showOfflineBar = false"
                class="text-white hover:bg-red-600"
              >
                <template #icon>
                  <n-icon :component="X" />
                </template>
              </n-button>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>

    <!-- 数据同步状态 -->
    <div
      v-if="showSyncStatus && pendingSyncCount > 0"
      class="fixed bottom-4 left-4 bg-blue-500 text-white rounded-lg p-3 shadow-lg z-30"
    >
      <div class="flex items-center space-x-2">
        <n-spin size="small" />
        <span class="text-sm">
          {{ pendingSyncCount }} Data item pending synchronization
        </span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { NButton, NIcon, NSpin } from 'naive-ui'
import { WifiOff, X } from '@vicons/tabler'
import { useErrorHandlerStore } from '@/stores/errorHandler'

interface Props {
  // 显示选项
  showOverlay?: boolean
  showBar?: boolean
  showSyncStatus?: boolean
  
  // 行为选项
  autoRetry?: boolean
  retryInterval?: number
  maxRetries?: number
}

const props = withDefaults(defineProps<Props>(), {
  showOverlay: true,
  showBar: true,
  showSyncStatus: true,
  autoRetry: true,
  retryInterval: 10000, // 10秒
  maxRetries: 5
})

const errorHandlerStore = useErrorHandlerStore()

// 本地状态
const checking = ref(false)
const showOfflineOverlay = ref(false)
const showOfflineBar = ref(true)
const retryCount = ref(0)
const pendingSyncCount = ref(0)
const autoRetryTimer = ref<ReturnType<typeof setInterval> | null>(null)

// 计算属性
const isOnline = computed(() => errorHandlerStore.networkStatus.isOnline)

// 方法
const checkConnection = async (): Promise<void> => {
  checking.value = true
  
  try {
    // 尝试发送健康检查请求
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 5000)
    
    const response = await fetch('/api/health', {
      method: 'HEAD',
      signal: controller.signal,
      cache: 'no-cache'
    })
    
    clearTimeout(timeoutId)
    
    const isOnline = response.ok
    errorHandlerStore.setOnlineStatus(isOnline)
    
    if (isOnline) {
      retryCount.value = 0
      showOfflineOverlay.value = false
      
      // 触发数据同步
      await syncPendingData()
    }
    
  } catch (error) {
    console.warn('[OfflineDetector] 连接检查失败:', error)
    errorHandlerStore.setOnlineStatus(false)
    retryCount.value++
  } finally {
    checking.value = false
  }
}

const continueOffline = (): void => {
  showOfflineOverlay.value = false
  showOfflineBar.value = true
}

const syncPendingData = async (): Promise<void> => {
  // 这里可以实现离线数据同步逻辑
  // 例如：同步离线时缓存的用户操作、表单数据等
  
  try {
    // 模拟数据同步
    const pendingOperations = getPendingOperations()
    pendingSyncCount.value = pendingOperations.length
    
    for (const operation of pendingOperations) {
      await syncOperation(operation)
      pendingSyncCount.value--
    }
    
    console.log('[OfflineDetector] 数据同步完成')
  } catch (error) {
    console.error('[OfflineDetector] 数据同步失败:', error)
  }
}

const getPendingOperations = (): any[] => {
  // 从本地存储获取待同步的操作
  try {
    const stored = localStorage.getItem('pendingOperations')
    return stored ? JSON.parse(stored) : []
  } catch {
    return []
  }
}

const syncOperation = async (_operation: any): Promise<void> => {
  // 同步单个操作
  // 这里应该根据操作类型调用相应的API
  await new Promise(resolve => setTimeout(resolve, 100)) // 模拟同步延迟
}

const startAutoRetry = (): void => {
  if (!props.autoRetry || autoRetryTimer.value) return
  
  autoRetryTimer.value = setInterval(() => {
    if (!isOnline.value && retryCount.value < props.maxRetries) {
      console.log(`[OfflineDetector] 自动重试连接 (${retryCount.value + 1}/${props.maxRetries})`)
      checkConnection()
    } else if (retryCount.value >= props.maxRetries) {
      stopAutoRetry()
    }
  }, props.retryInterval)
}

const stopAutoRetry = (): void => {
  if (autoRetryTimer.value) {
    clearInterval(autoRetryTimer.value)
    autoRetryTimer.value = null
  }
}

// 监听网络状态变化
watch(
  () => isOnline.value,
  (online, wasOnline) => {
    if (!online && wasOnline !== false) {
      // 刚刚离线
      if (props.showOverlay) {
        showOfflineOverlay.value = true
      }
      showOfflineBar.value = true
      
      // 开始自动重试
      startAutoRetry()
    } else if (online && wasOnline === false) {
      // 刚刚恢复在线
      showOfflineOverlay.value = false
      retryCount.value = 0
      
      // 停止自动重试
      stopAutoRetry()
      
      // 同步数据
      syncPendingData()
    }
  },
  { immediate: true }
)

// 生命周期
onMounted(() => {
  // 初始化网络监控
  errorHandlerStore.initNetworkMonitoring()
  
  // 检查初始连接状态
  if (!isOnline.value) {
    if (props.showOverlay) {
      showOfflineOverlay.value = true
    }
    startAutoRetry()
  }
})

onUnmounted(() => {
  stopAutoRetry()
})
</script>

<style scoped>
/* 过渡动画 */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.slide-down-enter-active,
.slide-down-leave-active {
  transition: transform 0.3s ease;
}

.slide-down-enter-from,
.slide-down-leave-to {
  transform: translateY(-100%);
}
</style>